import * as z from "zod";
import { isoDate } from "./common";

/**
 * A card show as anyone may read it: a listing read from a third-party
 * calendar, or a show a shop hosts. Times are instants; `timezone` is the zone
 * the show's own dates and times are read in.
 */

/** As much of the venue as its listing gives. */
export const PublicEventPlace = z.object({
  id: z.string(),
  name: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  /** Two letters, read from `province` whichever spelling it holds; null outside the US. */
  stateCode: z.string().nullable(),
  province: z.string().nullable(),
  zip: z.string().nullable(),
  countryCode: z.string().nullable(),
  /** Null, with `longitude`, for a venue no source pinned. */
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});
export type PublicEventPlace = z.output<typeof PublicEventPlace>;

export const PublicEventHost = z.object({
  id: z.string(),
  name: z.string().nullable(),
});
export type PublicEventHost = z.output<typeof PublicEventHost>;

const eventFields = {
  id: z.string(),
  title: z.string(),
  startsAt: isoDate,
  endsAt: isoDate,
  /** The event's zone, else its venue's; null when neither has one. */
  timezone: z.string().nullable(),
  /** A plain http(s) address, or null. */
  flyerUrl: z.string().nullable(),
  /** The show's own page elsewhere: a plain http(s) address, or null. */
  shareUrl: z.string().nullable(),
  /** The shop hosting the show; null for a listing read from a calendar. */
  host: PublicEventHost.nullable(),
  /** Tables shops have taken here; 0 for a listing read from a calendar. */
  tablesTaken: z.number(),
  place: PublicEventPlace,
};

export const PublicEvent = z.object(eventFields);
export type PublicEvent = z.output<typeof PublicEvent>;

export const PublicEventList = z.object({
  events: z.array(PublicEvent),
  /** Shows matching the filter: what pages it. */
  total: z.number(),
});
export type PublicEventList = z.output<typeof PublicEventList>;

export const PublicEventDetail = z.object({
  ...eventFields,
  /** As its calendar or its host wrote it: show it as text, never as markup. */
  description: z.string().nullable(),
});
export type PublicEventDetail = z.output<typeof PublicEventDetail>;

/** Every field optional: without any, the shows to come, soonest first. */
export interface PublicEventQuery {
  /** Only shows still going at or after this instant; one already past reads as now. */
  from?: Date;
  /** Only shows starting before this instant. */
  to?: Date;
  /** A US state's code or name. */
  state?: string;
  /** True: only shows a shop hosts. False: only listings read from calendars. */
  hosted?: boolean;
  /** 50 unless given; 200 at most. */
  limit?: number;
  offset?: number;
}
